import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import capitalize from 'lodash/capitalize';

import { parseDate, toTextualDateString } from '@/date';
import { toCurrencyString } from '@/number';
import { useCheckoutContext } from '@/contexts';
import {
  settingsManageUpdateAvaPlanPath,
  settingsManageUpdateChatPlanPath,
  settingsManageUpdatePlanPath
} from '@/routes';

import DiscountBanner from '@/components/Billing/DiscountBanner';
import Icon from '@/components/Icon';
import PeriodSelect from '@/components/Billing/PeriodSelect';
import ProratedSummary from '@/components/Billing/ProratedSummary';
import Popover from '@/components/Popover';
import Link from '@/components/Link';
import Tooltip from '@/components/Tooltip';

const propTypes = {
  subscriptionType: PropTypes.oneOf(['crm', 'ava', 'chat']).isRequired,
  isPlanStep: PropTypes.bool
};

const periodicityTexts = {
  monthly: 'mensal',
  quarterly: 'trimestral',
  half_yearly: 'semestral',
  yearly: 'anual'
};

function PaymentSummary({ subscriptionType, isPlanStep }) {
  const {
    currentPlanData,
    checkoutResumeData: {
      periodicity,
      name,
      finalValue,
      totalValue,
      currentExpirationDate,
      expirationDate,
      finalValueWithoutDiscount,
      addonsInfo,
      discounts,
      addonsDiscounts,
      currentDiscounts,
      prorateDiscounts,
      maxUsersCount,
      remainingDays,
      proratedInfo
    }
  } = useCheckoutContext();

  const allDiscounts = discounts.concat(addonsDiscounts);
  const hasDiscount = discounts.length > 0;
  const hasAnyDiscount = allDiscounts.length > 0;

  const withoutDiscountValueFormatted = toCurrencyString(finalValueWithoutDiscount);
  const expirationDateFormatted = toTextualDateString(parseDate(expirationDate));
  const finalValueFormatted = toCurrencyString(finalValue);
  const totalValueFormatted = toCurrencyString(totalValue);
  const periodicityText = periodicityTexts[periodicity];
  const currentPeriodicityText = periodicityTexts[currentPlanData.periodicity];
  const fullPlanText = `${name} ${maxUsersCount} ${capitalize(periodicityText)}`;

  return (
    <div
      id='payment-summary'
      className={
        classnames(
          'w-100',
          'text-small',
          'bg-light',
          'p-6 rounded'
        )
      }
    >
      {isPlanStep ? <ChoosePlan /> : <InvoiceInfo />}

      <h4 className='mt-2 mb-4'>Resumo</h4>

      <h5 className='mb-2 fw-normal'>Plano {fullPlanText}</h5>

      {proratedInfo && (
        <>
          <RowContent
            title={
              <div className='d-flex align-items-center text-body'>
                <span className='me-2'>Pró-rata</span>
              </div>
            }
          />

          <RowContent
            title={
              <div className='d-flex align-items-center text-body'>
                <span className='me-2'>Plano</span>

                <ProratedSummary
                  remainingDays={remainingDays}
                  planFullName={proratedInfo.planFullName}
                  currentPlanFullName={proratedInfo.currentPlanFullName}
                  valuePerMonth={proratedInfo.valuePerMonth}
                  currentValuePerMonth={proratedInfo.currentValuePerMonth}
                  diffValuePerMonth={proratedInfo.diffValuePerMonth}
                  diffValuePerDay={proratedInfo.diffValuePerDay}
                  value={proratedInfo.value}
                  valueWithoutCustomDiscount={proratedInfo.valueWithoutCustomDiscount}
                  discounts={discounts}
                  currentDiscounts={currentDiscounts}
                  prorateDiscounts={prorateDiscounts}
                  periodicityText={capitalize(currentPeriodicityText)}
                />
              </div>
            }
            content={toCurrencyString(proratedInfo.value)}
          />

          {proratedInfo.addonsProratedInfo.map((addonProratedInfo) => (
            <RowContent
              key={addonProratedInfo.addonName}
              title={
                <div className='d-flex align-items-center text-body'>
                  <span className='me-2'>{addonProratedInfo.addonName}</span>

                  <ProratedSummary
                    remainingDays={remainingDays}
                    planFullName={addonProratedInfo.fullAddonName}
                    currentPlanFullName={addonProratedInfo.currentFullAddonName}
                    valuePerMonth={addonProratedInfo.valuePerMonth}
                    currentValuePerMonth={addonProratedInfo.currentValuePerMonth}
                    diffValuePerMonth={addonProratedInfo.diffValuePerMonth}
                    diffValuePerDay={addonProratedInfo.diffValuePerDay}
                    value={addonProratedInfo.value}
                    valueWithoutCustomDiscount={addonProratedInfo.valueWithoutCustomDiscount}
                    discounts={addonProratedInfo.discounts}
                    currentDiscounts={addonProratedInfo.currentDiscounts}
                    prorateDiscounts={addonProratedInfo.prorateDiscounts}
                    periodicityText={capitalize(currentPeriodicityText)}
                  />
                </div>
              }
              content={toCurrencyString(addonProratedInfo.value)}
            />
          ))}

          <RowContent
            title={<span className='text-body'>Valor total</span>}
            content={toCurrencyString(proratedInfo.completeValue)}
          />

          <RowContent
            title={<span className='fw-normal'>Período</span>}
            content={`Até ${toTextualDateString(parseDate(currentExpirationDate))}`}
          />

          <hr className='mt-3 mb-2 opacity-100' />
        </>
      )}

      <RowContent
        title={<span className='text-body'>Plano</span>}
        content={
          <>
            {hasDiscount &&
              <span className='me-1'>
                <s>{withoutDiscountValueFormatted}</s>
              </span>
            }

            <span className={classnames({ 'text-success': hasDiscount })}>
              {finalValueFormatted}
            </span>
          </>
        }
      />

      {subscriptionType === 'chat' && <ChatFields addonsInfo={addonsInfo} />}

      <RowContent
        title={<span className='fw-normal'>Período</span>}
        content={`Até ${expirationDateFormatted}`}
      />

      <hr className='mt-3 mb-2 opacity-100' />

      {hasAnyDiscount &&
        allDiscounts.map((discount) => (
          <DiscountBanner
            key={discount.id}
            discount={discount}
            periodicityText={periodicityText}
          />
        ))
      }

      <div className='mt-4 d-flex align-items-baseline justify-content-between'>
        <h4 className='text-dark-gray'>Valor total</h4>

        <h2>{totalValueFormatted}</h2>
      </div>

      {
        proratedInfo && (
          <div className='d-flex justify-content-end mt-1'>
            <SummaryExplanation subscriptionType={subscriptionType} />
          </div>
        )
      }
    </div>
  );
}

function ChatFields({ addonsInfo }) {
  const { whatsappNumberAddon } = useCheckoutContext();

  return (
    <>
      <RowContent
        title={
          <div className='d-flex align-items-center text-body'>
            <span className='me-2'>Número de WhatsApp</span>

            <Tooltip
              content='Seu primeiro número é grátis!'
              placement='bottom'
            >
              <span>
                <Icon className='cursor-pointer' name='info' size='sm' />
              </span>
            </Tooltip>
          </div>
        }
        content={
          <>
            <span className='me-1'>
              <s>{toCurrencyString(whatsappNumberAddon.defaultValue)}</s>
            </span>

            <span className='text-success'>{toCurrencyString(0)}</span>
          </>
        }
      />

      {addonsInfo.map((addonInfo) => {
        if (addonInfo.productCode === 'whatsapp_number') {
          return (
            <RowContent
              title={<span className='text-body'>Valor por número extra do WhatsApp</span>}
              content={
                <>
                  {addonInfo.hasDiscounts &&
                    <span className='me-1'>
                      <s>{toCurrencyString(addonInfo.valueWithoutDiscount)}</s>
                    </span>
                  }

                  <span className={classnames({ 'text-success': addonInfo.hasDiscounts })}>
                    {toCurrencyString(addonInfo.value)}
                  </span>
                </>
              }
            />
          );
        }

        return null;
      })}
    </>
  );
}

function SummaryExplanation({ subscriptionType }) {
  const path = {
    crm: settingsManageUpdatePlanPath,
    chat: settingsManageUpdateChatPlanPath,
    ava: settingsManageUpdateAvaPlanPath
  };

  return (
    <Popover
      content={() => (
        <div className='w-100 text-dark-gray'>
          <h4 className='mb-3'>Entenda o cálculo</h4>

          <div className='mb-3'>
            Nessa tela, você irá contratar a renovação do seu plano. Isso significa que<br />
            você estará contratando o Agendor por mais um período.<br />
            Se você mudar o plano e houver diferença no valor, você também irá<br />
            pagar por essa diferença (pró-rata) direto nessa tela.
          </div>

          <div className='fw-bold'>
            Se você quiser contratar somente a mudança de plano sem contratar<br />
            um novo período, <Link href={path[subscriptionType]()}><u>clique aqui</u></Link>.
          </div>
        </div>
      )}
      className='mw-100'
      contentClassName='px-3 py-4 bg-dark-light rounded-1'
      trigger={['click']}
      placement='left-start'
      rootClose
      rootCloseEvent='mousedown'
      flip
    >
      <div className='text-dark-gray cursor-pointer text-underline'>
        Entenda o cálculo
      </div>
    </Popover>
  );
}

function ChoosePlan() {
  return (
    <>
      <h5 className='text-dark-gray'>Assinatura do plano</h5>

      <div className='mt-2'>
        <PeriodSelect />
      </div>

      <hr className='mt-2 mb-2 opacity-100' />
    </>
  );
}

function InvoiceInfo() {
  return (
    <div className={classnames(
      'd-flex',
      'align-items-center',
      'mb-2',
      'mt-1',
      'py-2',
      'ps-2',
      'pe-8',
      'bg-white',
      'rounded',
      'text-dark-gray'
    )}>
      <div>
        <Icon name='info' />
      </div>
      <div className='ms-3'>
        A nota fiscal será gerada e enviada para o seu e-mail
        automaticamente em até 24 horas após a confirmação do pagamento
      </div>
    </div>
  );
}

function RowContent({ title, content }) {
  return (
    <div className='d-flex align-items-baseline justify-content-between mb-1'>
      <div className='fw-bold text-dark-gray me-8'>{title}</div>

      <div className='ms-2'>{content}</div>
    </div>
  );
}

PaymentSummary.propTypes = propTypes;

export default PaymentSummary;
